// eslint-disable-next-line spaced-comment
/// <reference types="../../src/liburi" />
import * as URI from 'spotify-liburi';
import { EventEmitter } from '@spotify-internal/emitter';
import { logging } from '@js-sdk/debug-tools';
import { RepeatMode } from '../enums/repeat_mode';
const EXTRACT_RESTRICTION_NAME = /^disallow_([^]+)_reasons$/;
const debugLogger = logging.forTag('stream.PlaybackStateObserver');
/**
 * The Stream Track Window Observer class.
 *
 * Can be used to get details about the current and upcoming tracks, etc.
 */
export class PlaybackStateObserver extends EventEmitter {
    constructor(tpApiClient, options = {}) {
        super();
        this._tpApiClient = tpApiClient;
        this._numPreviousTracks = options.numPrevious || 2;
        this._numNextTracks = options.numNextTracks || 2;
        this._state = null;
        this._init();
    }
    static create(tpApiClient, options) {
        return new PlaybackStateObserver(tpApiClient, options);
    }
    _init() {
        this._tpApiClient.on("state_changed" /* TP_API_STATE_CHANGED */, this._onStateChanged.bind(this));
        this._tpApiClient.on("state_cleared" /* TP_API_STATE_CLEARED */, this._onStateCleared.bind(this));
    }
    _parseRestrictions(restrictions, paused) {
        const copy = {};
        for (const key in restrictions) {
            if (restrictions.hasOwnProperty(key) &&
                key !== 'disallow_resuming_reasons' &&
                key !== 'disallow_pausing_reasons') {
                copy[key] =
                    restrictions[key];
            }
        }
        // Since there is no "paused" state, we can get into situations where the
        // backend provided restrictions do not match with the local paused/resume
        // state. We therefore set these locally instead.
        if (paused) {
            copy.disallow_pausing_reasons = ['already_paused'];
        }
        else {
            copy.disallow_resuming_reasons = ['not_paused'];
        }
        return copy;
    }
    _parseDisallows(restrictions) {
        const res = {};
        for (const key in restrictions) {
            if (restrictions.hasOwnProperty(key)) {
                const restriction = restrictions[key];
                const disallow = !!(restriction && restriction.length);
                res[key.replace(EXTRACT_RESTRICTION_NAME, '$1')] = disallow;
            }
        }
        return res;
    }
    _onStateChanged(e) {
        const { position, paused, stateMachine, stateRef } = e.data;
        const states = (stateMachine && stateMachine.states) || [];
        if (!stateMachine || !states.length || !stateRef) {
            debugLogger.warn('_onStateChanged called with no current state', e);
            return;
        }
        let knownDuration = e.data.duration;
        // Timestamp is useful to update/offset position later
        const timestamp = Date.now();
        const currentIndex = stateRef.state_index;
        const currentState = states[currentIndex];
        const restrictions = this._parseRestrictions((currentState && currentState.restrictions) || {}, paused);
        const disallows = this._parseDisallows(restrictions);
        // TODO: It's not necessary to update the track window on each state change.
        const trackWindow = {
            current_track: this._getTrackMetadata(stateMachine, currentState, knownDuration),
            // FIXME: Improve _getTrackMetadata so it doesn't return null
            next_tracks: this._getNextStates(currentState, states)
                .map((state) => this._getTrackMetadata(stateMachine, state, null))
                .filter((trackMetadata) => !!trackMetadata),
            // FIXME: Improve _getTrackMetadata so it doesn't return null
            previous_tracks: this._getPreviousStates(currentState, states)
                .map((state) => this._getTrackMetadata(stateMachine, state, null))
                .filter((trackMetadata) => !!trackMetadata),
        };
        // If we don't have a known duration, we should default to the metadata
        // duration.
        if (!knownDuration) {
            knownDuration =
                trackWindow.current_track && trackWindow.current_track.duration_ms
                    ? trackWindow.current_track.duration_ms
                    : 0;
        }
        const repeatMode = this._getRepeatSetting(stateMachine);
        const state = {
            context: {
                uri: null,
                metadata: {},
            },
            position,
            duration: knownDuration,
            paused,
            shuffle: this._getShuffleSetting(stateMachine),
            repeat_mode: repeatMode,
            track_window: trackWindow,
            timestamp,
            restrictions,
            disallows,
        };
        const stateTrack = stateMachine.tracks[currentState.track];
        if (stateTrack && stateTrack.metadata) {
            if (stateTrack.metadata.context_uri) {
                state.context.uri = stateTrack.metadata.context_uri;
            }
            if (stateTrack.metadata.context_description) {
                state.context.metadata.context_description =
                    stateTrack.metadata.context_description;
            }
        }
        this._state = state;
        this.emit("state_changed" /* PLAYBACK_STATE_OBSERVER_STATE_CHANGED */, {
            state: state ? Object.assign({}, state) : null,
        });
    }
    _onStateCleared() {
        this._state = null;
        this.emit("state_changed" /* PLAYBACK_STATE_OBSERVER_STATE_CHANGED */, { state: null });
    }
    _getRepeatSetting(stateMachine) {
        const options = stateMachine.attributes.options;
        if (options.repeating_track) {
            return RepeatMode.TRACK;
        }
        if (options.repeating_context) {
            return RepeatMode.CONTEXT;
        }
        return RepeatMode.OFF;
    }
    _getShuffleSetting(stateMachine) {
        return stateMachine.attributes.options.shuffling_context;
    }
    _getNextStates(state, states) {
        const maxStates = this._numNextTracks;
        const list = [];
        let current = state;
        while (current) {
            if (!('show_next' in current.transitions) ||
                !current.transitions.show_next) {
                break;
            }
            const nextState = states[current.transitions.show_next.state_index];
            list.push(nextState);
            current = nextState;
            if (list.length >= maxStates) {
                break;
            }
        }
        return list;
    }
    _getPreviousStates(state, states) {
        const maxStates = this._numPreviousTracks;
        const list = [];
        let current = state;
        while (current) {
            if (!('show_prev' in current.transitions) ||
                !current.transitions.show_prev) {
                break;
            }
            const prevState = states[current.transitions.show_prev.state_index];
            list.unshift(prevState);
            current = prevState;
            if (list.length >= maxStates) {
                break;
            }
        }
        return list;
    }
    _getTrackMetadata(stateMachine, state, duration) {
        const track = stateMachine && stateMachine.tracks[state.track];
        if (!track || !track.metadata) {
            return null;
        }
        const metadata = track.metadata;
        // FIXME: Since we do not know the media formats right now, we only check
        // whether the item has the video manifest format.
        const isVideo = track.manifest && 'manifest_ids_video' in track.manifest;
        const trackType = track.track_type.toLowerCase();
        // Return a structure similar to what the Web API would return.
        const uri = URI.from(metadata.uri);
        const linkedFromUri = metadata.linked_from_uri
            ? URI.from(metadata.linked_from_uri)
            : null;
        return {
            id: uri ? URI.hexToId(uri.id) : null,
            uri: metadata.uri,
            type: uri && uri.type ? uri.type : 'unknown',
            linked_from: {
                uri: metadata.linked_from_uri || null,
                id: linkedFromUri ? URI.hexToId(linkedFromUri.id) : null,
            },
            media_type: isVideo ? 'video' : 'audio',
            track_type: trackType,
            name: metadata.name,
            duration_ms: duration ? duration : metadata.duration,
            artists: metadata.authors,
            album: {
                uri: metadata.group_uri,
                name: metadata.group_name,
                images: metadata.images,
            },
            is_playable: true,
        };
    }
    /**
     * Get the current state of playback.
     *
     * @return A promise that resolves the current player state.
     */
    getCurrentState() {
        const currentState = this._state;
        if (currentState && !currentState.paused) {
            // Position hasn't been updated since last state_change
            // so update position and timestamp to reflect current status
            const now = Date.now();
            const offsetPosition = currentState.position || 0 + (now - currentState.timestamp);
            currentState.position = offsetPosition;
            currentState.timestamp = now;
        }
        return Promise.resolve(this._state ? Object.assign({}, this._state) : null);
    }
}
//# sourceMappingURL=playback_state_observer.js.map